import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { onboardingAPI } from '../../../services/api';
import { toast } from 'react-toastify';
import Spinner from '../../UIKit/Spinner';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  Paper,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CardContent,
  CardHeader,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import Button from '../../UIKit/Button';
import Card from '../../UIKit/Card';

// Функция для мультиязычного вывода
const getLocalized = (val, currentLang) => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  return val[currentLang] || val['en'] || val['ru'] || '';
};

const OnboardingPreview = ({ id = null, onClose }) => {
  const params = useParams();
  const guideId = id || params.id;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['admin', 'onboarding', 'common']);
  const currentLang = i18n.language || 'en';

  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);

  // Загрузка данных гида
  useEffect(() => {
    const fetchGuide = async () => {
      try {
        setLoading(true);
        const response = await onboardingAPI.admin.getGuideById(guideId);
        if (response && response.data) {
          // Сортируем шаги по полю order
          const sortedSteps = [...response.data.steps].sort(
            (a, b) => a.order - b.order
          );
          setGuide({
            ...response.data,
            steps: sortedSteps,
          });
          setLoading(false);
        } else {
          console.error('Ошибка загрузки гида: ответ пустой или некорректный');
          toast.error(t('admin:onboarding.loadError'));
          setLoading(false);
        }
      } catch (error) {
        console.error('Ошибка при загрузке гида:', error);
        toast.error(t('admin:onboarding.loadError'));
        setLoading(false);
      }
    };

    if (guideId) {
      fetchGuide();
    } else {
      navigate('/admin');
    }
  }, [guideId, navigate, t]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // --- UI ---
  const content = (
    <Container maxWidth="md">
      <Box py={3}>
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardHeader
            title={getLocalized(guide?.name, currentLang)}
            subheader={getLocalized(guide?.description, currentLang)}
            action={
              <Box display="flex" alignItems="center" gap={1}>
                <Chip
                  label={
                    guide?.active
                      ? t('admin:onboarding.statusActive')
                      : t('admin:onboarding.statusInactive')
                  }
                  color={guide?.active ? 'success' : 'default'}
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={
                    guide?.autostart
                      ? t('admin:onboarding.autostartYes')
                      : t('admin:onboarding.autostartNo')
                  }
                  color={guide?.autostart ? 'info' : 'default'}
                  size="small"
                />
              </Box>
            }
          />
          <CardContent>
            <Box mb={2}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                gutterBottom
              >
                {t('admin:onboarding.pageUrl')}:
              </Typography>
              <Typography variant="body2">
                {guide?.page?.url || '*'}{' '}
                {guide?.page?.tabId ? `(${guide.page.tabId})` : ''}
              </Typography>
            </Box>
            <Box mb={2}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                gutterBottom
              >
                {t('admin:onboarding.roles')}:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {guide?.roles?.map((role) => (
                  <Chip
                    key={role}
                    label={t(
                      `admin:onboarding.role${
                        role.charAt(0).toUpperCase() + role.slice(1)
                      }`
                    )}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Typography variant="h6" gutterBottom>
          {t('admin:onboarding.steps')} ({guide?.steps?.length || 0})
        </Typography>
        <Paper elevation={0} sx={{ p: 3, borderRadius: '16px' }}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {guide?.steps?.map((step, index) => (
              <Step key={index}>
                <StepLabel
                  onClick={() => setActiveStep(index)}
                  sx={{ cursor: 'pointer' }}
                >
                  <Typography variant="subtitle1">
                    {getLocalized(step.title, currentLang)}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Box sx={{ mb: 2 }}>
                    <Typography>{getLocalized(step.content, currentLang)}</Typography>
                    <Box mt={2}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                      >
                        {t('admin:onboarding.stepSelector')}: {step.selector}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                      >
                        {t('admin:onboarding.stepPlacement')}: {step.placement}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                      >
                        {t('admin:onboarding.stepRoute')}: {step.route || '*'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                    <Button
                      variant="text"
                      disabled={index === 0}
                      onClick={handleBack}
                      startIcon={<PrevIcon />}
                    >
                      {t('onboarding:buttons.prev')}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={index === (guide?.steps?.length || 0) - 1}
                      endIcon={<NextIcon />}
                    >
                      {t('onboarding:buttons.next')}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Paper>
      </Box>
    </Container>
  );

  if (loading) return <Spinner />;
  if (!guide) {
    return (
      <Container maxWidth="md">
        <Box display="flex" flexDirection="column" alignItems="center" py={5}>
          <Typography variant="h6" color="error" gutterBottom>
            {t('admin:onboarding.guideNotFound')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<BackIcon />}
            onClick={onClose}
            sx={{ mt: 2 }}
          >
            {t('admin:onboarding.backToList')}
          </Button>
        </Box>
      </Container>
    );
  }

  return content;
};

// Модальное окно для предпросмотра гида
export const PreviewOnboardingDialog = ({ open, onClose, guideId }) => {
  const { t } = useTranslation(['admin', 'common']);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">
            {t('admin:onboarding.previewTitle')}
          </Typography>
          <IconButton edge="end" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <OnboardingPreview id={guideId} onClose={onClose} />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          {t('common:actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
